import React, { useState, useEffect } from "react";
import { IonPage, IonContent, IonButton, IonHeader, IonText, IonGrid, useIonAlert } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useForm } from "react-hook-form";

import './PhoneVerificationPage.css';

import AppHeader from "../../../components/AppHeader/AppHeader";
import KeyPad from "../../../components/KeyPad/KeyPad";
import { useHistory, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../services/redux/hook";
import { setUser } from "../../../services/redux/slices/user/user";
import { PhoneTokenRequestParameters, PhoneTokenVerificationParameters, UserInfo } from "../../../types/types";
import { ebioroApi, useVerifyPhoneTokenMutation } from "../../../services/api/ebioro/ebioro";
import * as Sentry from '@sentry/react';
import { parsePhoneNumber } from 'awesome-phonenumber';


const PhoneVerificationPage: React.FC<any> = () => {

    const history = useHistory();
    const location = useLocation();
    const dispatch = useAppDispatch()
    const { t, i18n } = useTranslation();
    const user:UserInfo = useAppSelector((state) => state.user);
    const [phoneVerifyTokenId, setPhoneVerifyTokenId] = useState('');
    const [pins, setPins] = useState(Array<string>(6).fill(''));
    const [counter, setCounter] = React.useState(30);
    const [present] = useIonAlert();
    const [auth_token, setAuthToken] = useState<string>();

    const [verifyPhoneToken] = useVerifyPhoneTokenMutation();

    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error.data}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        setPins(Array<string>(6).fill(''));
                        return;
                    }
                }
            ],

        })

    }

    useEffect(() => {
        counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
    }, [counter]);


    const {
        handleSubmit,
        control,
        setValue,
        register,
        getValues,
        formState: { errors }
    } = useForm({ defaultValues: { verifyCode: '' } });

    const resendOTP = async () => {

        setPins(Array<string>(6).fill(''));
        setCounter(30)
        
        const params: PhoneTokenRequestParameters = {

            email: user.email,
            mobile: user.mobile_number,
            auth_token: auth_token as string

        }

        try {

            const response = await dispatch(ebioroApi.endpoints.getPhoneVerificationToken.initiate(params, { forceRefetch: true })).unwrap();
            setPhoneVerifyTokenId(response.message);
            setAuthToken(response.auth_token);
            
        } catch (error) {
            Sentry.captureException(error);
            presentErrorAlert({ data: t('application.uncaught.ERROR') })
            return
        }

    }

    const onSubmit = async () => {

        const inputCombination = pins.map((i) => String(i)).join('')

        const verifyCode = inputCombination;

        const params:PhoneTokenVerificationParameters = {
            
            tokenId:phoneVerifyTokenId,
            code:verifyCode,
            email:user.email,
            mobile:user.mobile_number,
            auth_token:auth_token as string
        }

        try {

            const response = await verifyPhoneToken(params).unwrap();

            if (response.message === 'verified'){

                const updatedUser = { ...user }

                updatedUser.is_mobile_number_verified = true;

                //now that the phone is verified.Set the user country code
                const pn = parsePhoneNumber(updatedUser.mobile_number);
                const regionCode = pn.regionCode
                updatedUser.country_code2 = regionCode;
                dispatch(setUser(updatedUser))

                history.push({
                    pathname:'/wallet-generate-intro',
                    state:{
                        detail:{
                            auth_token:response.auth_token}
                        }
                })
            }

            else {
                presentErrorAlert({ data: t('registration.phone.verification.VERIFY_ERROR') })
                return;
            }
            
        } catch (error) {

            Sentry.captureException(error)
            presentErrorAlert({ data: t('registration.phone.verification.VERIFY_ERROR') })
            return;
            
        }
        // }
    }

    const handleClick = (value: any) => {
        let tempCode = pins;
        for (var i = 0; i < tempCode.length; i++) {
            if (tempCode[i] == '') {
                tempCode[i] = value
                break;
            }
            else {
                continue
            }
        }
        setPins([...tempCode])
    }

    const handleRemove = () => {
        let tempCode = pins;
        for (let i = tempCode.length - 1; i >= 0; i--) {
            if (tempCode[i] != '') {
                tempCode[i] = '';
                break
            }
            else {
                continue
            }
        }
        setPins([...tempCode])
    }

    const handleChange = (element: any, index: number) => {
        if (isNaN(element.value)) return false;

        setPins([...pins.map((d, idx) => (idx === index ? element.value : d))]);

        if (element.nextSibling) {
            element.nextSibling.focus();
        }
    };




    useEffect(() => {
        let count = 0
        for (let i = 0; i < pins.length; i++) {
            if (pins[i] != '') {
                count += 1
            }
        }
        if (count === 6) {
            onSubmit()
        }
    }, [pins])

    useEffect(()=>{

        const location:any = history.location;
        const authToken = location.state.detail.auth_token;
        setAuthToken(authToken);
        const verifyTokenId = location.state.detail.verifyTokenId;
        setPhoneVerifyTokenId(verifyTokenId);

    },[])

    return (
        <form onSubmit={handleSubmit(onSubmit)} style={{ padding: 18 }}>
            <IonPage>
                <IonHeader className="ion-no-border">


                    <AppHeader
                        title={t('registration.phone.verification.HEADER') as string}
                        goBack={true}
                    >
                    </AppHeader>


                </IonHeader>

                <IonContent scrollY={false}>

                    <div className="ion-text-center otp_inputs">
                        <div className="otp-boxes-container">
                            {pins.map((value, index) => (
                                <div
                                    key={index}
                                    className={`otp-box ${value !== '' ? 'otp-box-filled' : ''}`}
                                >
                                    <span className="otp-text">
                                        {value || ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <IonText color="medium" className="ion-text-center">
                        <p className="phonePageInfoText ion-text-wrap">{t('registration.phone.verification.SUBHEADER')} {user.mobile_number}</p>
                    </IonText>

                    <div className="ion-text-center">

                        {counter != 0
                            ? <IonButton
                                disabled={true}
                                onClick={() => resendOTP()}
                                color="primary"
                                fill="clear"
                            >
                                {t('registration.phone.verification.BTN_RESEND_CODE')}
                            </IonButton>
                            :
                            <IonButton
                                disabled={false}
                                onClick={() => resendOTP()}
                                color="primary"
                                fill="clear"
                            >
                                {t('registration.phone.verification.BTN_RESEND_CODE')}
                            </IonButton>
                        }
                        <div> {t('registration.phone.verification.BTN_RESEND_CODE_IN')} {counter}s</div>

                    </div>

                    <IonGrid>
                        <KeyPad handleClick={handleClick} handleRemove={handleRemove} compact />
                    </IonGrid>

                </IonContent>

            </IonPage>
        </form>
    )
}

export default PhoneVerificationPage
